import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useRouter } from 'next/router'

import Toast, { ToastData, ToastVariant } from '@/components/base/toast'

type ToastInput = {
  message: string
  variant: ToastVariant
  duration?: number
}

// eslint-disable-next-line no-unused-vars
type ShowToast = (input: ToastInput) => void

type ToastContextValue = {
  showToast: ShowToast
  dismissToast: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const ToastProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter()
  const [toast, setToast] = useState<ToastData | null>(null)
  const [toastOpen, setToastOpen] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toastIdRef = useRef(0)

  const dismissToast = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = null
    setToastOpen(false)
  }, [])

  const showToast = useCallback((input: ToastInput) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    const id = ++toastIdRef.current
    const nextToast = { id, message: input.message, variant: input.variant }
    setToast(nextToast)
    setToastOpen(true)
    timeoutRef.current = setTimeout(
      () => {
        setToastOpen(false)
        timeoutRef.current = null
      },
      input.duration ?? (input.variant === 'error' ? 6000 : 4000)
    )
  }, [])

  useEffect(() => {
    router.events.on('routeChangeStart', dismissToast)

    return () => router.events.off('routeChangeStart', dismissToast)
  }, [dismissToast, router.events])

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    },
    []
  )

  const contextValue = useMemo(() => ({ dismissToast, showToast }), [dismissToast, showToast])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Toast toast={toast} open={toastOpen} onClose={dismissToast} />
    </ToastContext.Provider>
  )
}

const useToast = () => {
  const context = useContext(ToastContext)

  if (!context) throw new Error('useToast must be used within a ToastProvider')

  return context
}

export { ToastProvider, useToast }
